import { useState, useCallback, useEffect } from 'react';

import ResizeObserver from 'lib/resize-observer';
import { Dimensions } from './component-dimensions.constants';

export const useContainerDimensions = ({
  aspectRatio,
  heightOffset = 0,
  onComplete,
}: {
  aspectRatio: number;
  heightOffset?: number;
  onComplete?: () => void;
}) => {
  const [elementDimensions, setElementDimensions] = useState<Dimensions>();
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null
  );

  const containerWidth = containerElement?.offsetWidth || 0;
  const containerHeight = containerElement?.offsetHeight || 0;

  const computeSize = useCallback(
    () => {
      const maxWidth = containerWidth;
      const maxHeight = containerHeight - heightOffset;

      if (maxWidth / maxHeight > aspectRatio) {
        setElementDimensions({
          width: maxHeight * aspectRatio,
          height: maxHeight,
        });
      } else {
        setElementDimensions({
          width: maxWidth,
          height: maxWidth / aspectRatio,
        });
      }
      onComplete?.();
    },
    //eslint-disable-next-line
    [containerHeight, containerWidth, heightOffset]
  );

  useEffect(() => {
    if (containerHeight > 0 && containerWidth > 0) {
      computeSize();
    }
  }, [containerHeight, containerWidth, computeSize]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(computeSize);

    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
    };
  }, [containerElement, computeSize]);

  return {
    elementDimensions,
    setContainerElement,
    style: elementDimensions
      ? {
          width: `${elementDimensions.width}px`,
          height: `${elementDimensions.height}px`,
        }
      : undefined,
  };
};
